import type { Nullable } from "models-webgl2";
import { EventEmitter } from "utils/helpers/classes/event-emitter";
import { applyCSSToNode } from "utils/dom/apply-css-to-node";
import { removeCSSFromNode } from "utils/dom/remove-css-from-node";
import { Vec2 } from "utils/math/vec2";
import {
  Webgl2Component,
  MeasurementComponent,
  MeasurementsComponent,
  MeasurementSelectionComponent,
} from "../../webgl2-component";
import { Webgl2dRenderer } from "../webgl2d-renderer";
import { Webgl2Tool } from "./webgl2-tool";

const MEASUREMENT_SELECT_DISTANCE_PX = 28;

const getDistanceToSegment = (point: Vec2, start: Vec2, end: Vec2) => {
  const x1 = start.x;
  const y1 = start.y;
  const x2 = end.x;
  const y2 = end.y;
  const px = point.x;
  const py = point.y;

  const dx = x2 - x1;
  const dy = y2 - y1;

  if (!dx && !dy) {
    return Math.hypot(px - x1, py - y1);
  }

  const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
  const clampedT = Math.max(0, Math.min(1, t));
  const projectionX = x1 + clampedT * dx;
  const projectionY = y1 + clampedT * dy;

  return Math.hypot(px - projectionX, py - projectionY);
};

export type MeasurementToolEmitter = {
  onDown: (event: MouseEvent | TouchEvent) => void;
  onMove: (event: MouseEvent | TouchEvent) => void;
  onUp: (event: MouseEvent | TouchEvent) => void;
  onSelect: (component: MeasurementComponent) => void;
};

export class MeasurementTool extends Webgl2Tool {
  private _currComponent: Nullable<Webgl2Component>;

  private _emitter = new EventEmitter<MeasurementToolEmitter>();
  public get events() {
    return this._emitter.events;
  }

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();
  }

  private _findNearestMeasurementComponent(mousePos: Vec2): Nullable<MeasurementComponent> {
    const measurementsRoot = this._renderer.components.find(
      (c) => c instanceof MeasurementsComponent,
    ) as MeasurementsComponent | undefined;

    if (!measurementsRoot) return null;

    const scale = this._renderer.camera.scale || 1;
    const maxDistance = MEASUREMENT_SELECT_DISTANCE_PX / scale;

    let nearestComponent: MeasurementComponent | null = null;
    let nearestDistance = Number.POSITIVE_INFINITY;

    measurementsRoot.forEachChildren((child) => {
      if (!(child instanceof MeasurementComponent)) return;
      if (!child.isValidPos) return;

      const startPos = child.startPos;
      const endPos = child.endPos;
      const distance = getDistanceToSegment(mousePos, startPos, endPos);

      if (distance > maxDistance) return;

      if (!nearestComponent || distance < nearestDistance) {
        nearestComponent = child;
        nearestDistance = distance;
      }
    });

    return nearestComponent;
  }

  public onAssign() {
    applyCSSToNode(this._renderer.parent, { cursor: "crosshair" });
  }

  public onUnAssign() {
    removeCSSFromNode(this._renderer.parent, "cursor");
  }

  public onMouseDown(e: MouseEvent) {
    return this._handleDown(e);
  }

  public onMouseMove(e: MouseEvent) {
    return this._handleMove(e);
  }

  public onMouseEnded(e: MouseEvent) {
    return this._handleUp(e);
  }

  public onTouchStart(e: TouchEvent) {
    return this._handleDown(e);
  }

  public onTouchMove(e: TouchEvent) {
    return this._handleMove(e);
  }

  public onTouchEnded(e: TouchEvent) {
    return this._handleUp(e);
  }

  private _handleDown(e: MouseEvent | TouchEvent) {
    const componentUnderMouse = this._renderer.findComponentUnderMouse(e);

    const parent = Webgl2dRenderer.global.getRootParent(componentUnderMouse).slice(-1)[0];

    if (parent instanceof MeasurementComponent && parent !== this._renderer.selectedComponent) {
      this._emitter.emit("onSelect", parent);
      return false;
    }

    if (parent instanceof MeasurementSelectionComponent && componentUnderMouse?.onMouseDown) {
      this._currComponent = componentUnderMouse;
      componentUnderMouse.onMouseDown(e);
      return;
    }

    const mousePos = this._renderer.camera.getGlobalMousePixelPosition(e);
    const nearestMeasurement = this._findNearestMeasurementComponent(mousePos);
    if (nearestMeasurement && nearestMeasurement !== this._renderer.selectedComponent) {
      this._emitter.emit("onSelect", nearestMeasurement);
      return false;
    }

    this._emitter.emit("onDown", e);
  }

  private _handleMove(e: MouseEvent | TouchEvent) {
    if (this._currComponent) {
      this._currComponent.onMouseMove?.(e);
      return;
    }

    this._emitter.emit("onMove", e);
  }

  private _handleUp(e: MouseEvent | TouchEvent) {
    if (this._currComponent) {
      this._currComponent?.onMouseUp?.(e);
    } else {
      this._emitter.emit("onUp", e);
    }

    this._currComponent = null;
  }
}

export default MeasurementTool;
